import React from "react";
import { Column } from "Components/Common/CommonTableIndexWise";
import { FormFieldConfig } from "Components/Common/CommonForm";
import Commonbadge from "Components/Common/Commonbadge";
import CommonButton from "Components/Common/CommonButton";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";
import { formatDate } from "common/service/CommonService";
import { validations } from "validations/FormValidations";

export const DUMMY_POST_STUDY_SUBJECTS = [
  {
    subjectno: "1",
    initials: "RP",
    assessmentdate: "2026-09-10",
    physicalexamstatus: "completed",
    labassessmentstatus: "completed",
    assessmentstatus: "completed",
  },
  {
    subjectno: "2",
    initials: "AK",
    assessmentdate: "2026-09-12",
    physicalexamstatus: "completed",
    labassessmentstatus: "pending",
    assessmentstatus: "in progress",
  },
  {
    subjectno: "3",
    initials: "JS",
    assessmentdate: "2026-09-14",
    physicalexamstatus: "pending",
    labassessmentstatus: "pending",
    assessmentstatus: "pending",
  },
  {
    subjectno: "4",
    initials: "MG",
    assessmentdate: "2026-09-15",
    physicalexamstatus: "completed",
    labassessmentstatus: "completed",
    assessmentstatus: "completed",
  },
  {
    subjectno: "5",
    initials: "DV",
    assessmentdate: "2026-09-15",
    physicalexamstatus: "pending",
    labassessmentstatus: "pending",
    assessmentstatus: "pending",
  },
  {
    subjectno: "6",
    initials: "SS",
    assessmentdate: "2026-09-16",
    physicalexamstatus: "completed",
    labassessmentstatus: "pending",
    assessmentstatus: "in progress",
  },
];

export const postStudyAssessmentColumns = (
  handlePerformAssessment: (row: any) => void
): Column<any>[] => [
    {
      key: "subjectno",
      label: "SUBJECT NO.",
      render: (_: any, row: any) => {
        const val = row.subjectno || row.subjectId || "--";
        const initials = row.initials || row.subjectname?.slice(0, 2).toUpperCase() || "SJ";
        return (
          <div
            className="d-flex align-items-center gap-2 cursor-pointer hover-underline"
            onClick={() => handlePerformAssessment(row)}
          >
            <div
              className="avatar-xs rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center fw-bold"
              style={{ width: "28px", height: "28px", fontSize: "11px", flexShrink: 0 }}
            >
              {initials}
            </div>
            <span className="fw-bold text-primary">
              {val}
            </span>
          </div>
        );
      },
    },
    {
      key: "assessmentdate",
      label: "ASSESSMENT DATE",
      render: (_: any, row: any) => {
        const date = row.assessmentdate;
        return (
          <span className="text-dark">
            <i className="ri-calendar-line text-primary me-1" />
            {date ? formatDate(date) : "--"}
          </span>
        );
      },
    },
    {
      key: "physicalexamstatus",
      label: "PHYSICAL EXAM STATUS",
      textCenter: true,
      render: (_: any, row: any) => {
        const status = row.physicalexamstatus || "-";
        return (
          <div className="d-flex justify-content-center align-items-center">
            <Commonbadge status={status} style={{ width: "110px", fontSize: "12px" }} />
          </div>
        );
      },
    },
    {
      key: "labassessmentstatus",
      label: "LAB ASSESSMENT STATUS",
      textCenter: true,
      render: (_: any, row: any) => {
        const status = row.labassessmentstatus || "-";
        return (
          <div className="d-flex justify-content-center align-items-center">
            <Commonbadge status={status} style={{ width: "110px", fontSize: "12px" }} />
          </div>
        );
      },
    },
    {
      key: "assessmentstatus",
      label: "OVERALL STATUS",
      textCenter: true,
      render: (_: any, row: any) => {
        const status = row.assessmentstatus || "-";
        return (
          <div className="d-flex justify-content-center align-items-center">
            <Commonbadge status={status} style={{ width: "110px", fontSize: "12px" }} />
          </div>
        );
      },
    },
    {
      key: "actions",
      label: "ACTIONS",
      textCenter: true,
      render: (_: any, row: any) => {
        const status = (row.assessmentstatus || "").toLowerCase();
        const isCompleted = status === "completed";
        return (
          <div className="d-flex justify-content-center align-items-center">
            <CommonButton
              module={PERMISSION_MODULES.POST_STUDY_ASSESSMENT}
              action={PERMISSION_ACTIONS.VIEW}
              size="md"
              color={isCompleted ? "dark" : "primary"}
              outline={isCompleted}
              style={{ borderRadius: 30 }}
              onClick={() => handlePerformAssessment(row)}
            >
              <i className={isCompleted ? "ri-eye-line" : "ri-pencil-line"} />
              {isCompleted ? " View Assessment" : " Perform Assessment"}
            </CommonButton>
          </div>
        );
      },
    },
  ];

export const getPhysicalExamFields = (): FormFieldConfig[] => [
  {
    name: "position",
    label: "Position",
    type: "radio",
    options: [
      { label: "Sitting", value: "Sitting" },
      { label: "Supine", value: "Supine" },
    ],
    required: true,
    rules: validations.postStudyAssessment.position,
  },
  {
    name: "actualTime",
    label: "Actual Time (Hrs.)",
    type: "time",
    placeholder: "--:--",
    required: true,
    rules: validations.postStudyAssessment.actualTime,
  },
  {
    name: "bloodPressure",
    label: "Blood Pressure (mmHg)",
    type: "text",
    placeholder: "e.g. 120/80",
    required: true,
    rules: validations.postStudyAssessment.bloodPressure,
  },
  {
    name: "pulseRate",
    label: "Pulse Rate (bpm)",
    type: "text",
    placeholder: "e.g. 72",
    required: true,
    rules: validations.postStudyAssessment.pulseRate,
  },
  {
    name: "respirationRate",
    label: "Respiration Rate (/min)",
    type: "text",
    placeholder: "e.g. 16",
    required: true,
    rules: validations.postStudyAssessment.respirationRate,
  },
  {
    name: "oralTemp",
    label: "Oral Temp (°F)",
    type: "text",
    placeholder: "e.g. 98.6",
    required: true,
    rules: validations.postStudyAssessment.oralTemp,
  },
  {
    name: "vitalWellBeing",
    label: "Well Being Assessment",
    type: "radio",
    options: [
      { label: "Well", value: "Well" },
      { label: "Unwell", value: "Unwell" },
    ],
    required: true,
    rules: validations.postStudyAssessment.vitalWellBeing,
  },
  {
    name: "respiratorySystem",
    label: "Respiratory System",
    type: "radio",
    options: [
      { label: "Normal", value: "Normal" },
      { label: "Abnormal", value: "Abnormal" },
    ],
    required: true,
    rules: validations.postStudyAssessment.respiratorySystem,
  },
  {
    name: "gastroIntestinalSystem",
    label: "Gastro Intestinal System",
    type: "radio",
    options: [
      { label: "Normal", value: "Normal" },
      { label: "Abnormal", value: "Abnormal" },
    ],
    required: true,
    rules: validations.postStudyAssessment.gastroIntestinalSystem,
  },
  {
    name: "cardiovascularSystem",
    label: "Cardiovascular System",
    type: "radio",
    options: [
      { label: "Normal", value: "Normal" },
      { label: "Abnormal", value: "Abnormal" },
    ],
    required: true,
    rules: validations.postStudyAssessment.cardiovascularSystem,
  },
  {
    name: "centralNervousSystem",
    label: "Central Nervous System",
    type: "radio",
    options: [
      { label: "Normal", value: "Normal" },
      { label: "Abnormal", value: "Abnormal" },
    ],
    required: true,
    rules: validations.postStudyAssessment.centralNervousSystem,
  },
  {
    name: "wellBeingAssessment",
    label: "Well-being Assessment",
    type: "radio",
    options: [
      { label: "Well", value: "Well" },
      { label: "Unwell", value: "Unwell" },
    ],
    required: true,
    rules: validations.postStudyAssessment.wellBeingAssessment,
  },
  {
    name: "participantStatus",
    label: "Participant's Status",
    type: "radio",
    options: [
      { label: "Fit", value: "Fit" },
      { label: "Unfit", value: "Unfit" },
    ],
    required: true,
    rules: validations.postStudyAssessment.participantStatus,
  },
  {
    name: "remarks",
    label: "Remarks",
    type: "textarea",
    placeholder: "Enter physical examination remarks...",
    rules: validations.postStudyAssessment.remarks,
  },
];

export const getLaboratoryExamFields = (
  clinicallySignificantAbnormal?: string
): FormFieldConfig[] => [
    {
      name: "bloodSamplesCollected",
      label: "Blood samples collected",
      type: "radio",
      options: [
        { label: "Yes", value: "Yes" },
        { label: "No", value: "No" },
      ],
      required: true,
      rules: validations.postStudyAssessment.bloodSamplesCollected,
    },
    {
      name: "bloodVolumeCollected",
      label: "Volume of Blood collected (mL)",
      type: "text",
      placeholder: "e.g. 5",
      rules: validations.postStudyAssessment.bloodVolumeCollected,
    },
    {
      name: "clinicallySignificantAbnormal",
      label: "Any clinically significant abnormal value(s)",
      type: "radio",
      options: [
        { label: "Yes", value: "Yes" },
        { label: "No", value: "No" },
      ],
      required: true,
      rules: validations.postStudyAssessment.clinicallySignificantAbnormal,
    },
    ...(clinicallySignificantAbnormal === "Yes"
      ? [
        {
          name: "abnormalDetails",
          label: "If Yes, provide details",
          type: "textarea" as const,
          placeholder: "Provide details of abnormal values...",
          required: true,
          rules: validations.postStudyAssessment.abnormalDetails,
        },
      ]
      : []),
  ];
